import * as React from 'react';
import { Button, Checkbox, Input, NumberField } from '@heroui/react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { isLocalLetterOptionId, numberToLetterId } from '../utils';
import type { MultipleChoiceQuestion, QuestionnaireType, QuestionOption } from '../types';

export interface MultipleChoiceControlProps {
  question: MultipleChoiceQuestion;
  disabled?: boolean;
  showCorrectAnswer?: boolean;
  showScore?: boolean;
  questionnaireType?: QuestionnaireType;
  onUpdate: (updates: Partial<MultipleChoiceQuestion>) => void;
}

export function MultipleChoiceControl({
  question,
  disabled,
  showCorrectAnswer,
  showScore,
  questionnaireType,
  onUpdate,
}: MultipleChoiceControlProps) {
  const options = question.options || [];
  const correctAnswers = question.correctAnswers || [];
  const showSelectionLimits =
    questionnaireType === 'survey' || questionnaireType === 'vote' || questionnaireType === 'assessment';
  const isCorrectAnswerRequired = questionnaireType === 'quiz' || questionnaireType === 'exam';

  const emitWithRenumberedOptions = (opts: QuestionOption[]) => {
    const prevIds = opts.map((o) => o.id?.trim() ?? '');
    const updated = opts.map((opt, idx) => {
      const raw = opt.id?.trim() ?? '';
      const newId = raw === '' || isLocalLetterOptionId(raw) ? numberToLetterId(idx + 1) : raw;
      return { ...opt, id: newId };
    });
    const idMap = new Map<string, string>();
    for (let i = 0; i < prevIds.length; i++) {
      const from = prevIds[i] || numberToLetterId(i + 1);
      idMap.set(from, updated[i]?.id ?? from);
    }
    const updates: Partial<MultipleChoiceQuestion> = { options: updated };
    if (correctAnswers.length) {
      const mapped = correctAnswers.map((id) => (typeof id === 'string' ? idMap.get(id) ?? id : id));
      const changed = mapped.some((id, idx) => id !== correctAnswers[idx]);
      if (changed) updates.correctAnswers = mapped as string[];
    }
    onUpdate(updates);
  };

  const handleOptionUpdate = (index: number, field: 'label' | 'value', value: string) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value.trim() };
    onUpdate({ options: newOptions });
  };

  const isCorrectAnswer = (optionId: string) => correctAnswers.includes(optionId);

  const handleCorrectAnswerToggle = (optionId: string, checked: boolean) => {
    let next = [...correctAnswers];
    if (checked) {
      if (!next.includes(optionId)) next.push(optionId);
    } else {
      next = next.filter((v) => v !== optionId);
    }
    onUpdate({ correctAnswers: next });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="text-sm font-medium text-foreground">选项配置</div>
        <div className="space-y-2">
          {options.map((option, index) => (
            <div key={option.id || index} className="flex items-center gap-2">
              <Input
                className="min-w-0 flex-1"
                value={option.label}
                onChange={(e) => handleOptionUpdate(index, 'label', e.target.value)}
                placeholder={`选项 ${index + 1}`}
                disabled={disabled}
              />
              <Button
                isIconOnly
                type="button"
                variant="ghost"
                size="sm"
                onPress={() => {
                  if (options.length <= 2) return;
                  emitWithRenumberedOptions(options.filter((_, i) => i !== index));
                }}
                isDisabled={disabled || options.length <= 2}
                aria-label="删除选项"
              >
                <IconTrash className="size-4" />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onPress={() => emitWithRenumberedOptions([...options, { id: '', label: '', value: '' }])}
            isDisabled={disabled}
          >
            <IconPlus className="mr-2 size-4" />
            添加选项
          </Button>
          {showCorrectAnswer ? (
            <div
              className={`flex flex-1 flex-wrap items-center gap-3 rounded-md border px-3 py-2 ${
                isCorrectAnswerRequired && correctAnswers.length === 0
                  ? 'border-warning/60'
                  : 'border-divider'
              }`}
            >
              <span className="shrink-0 text-sm font-medium">
                正确答案{isCorrectAnswerRequired ? <span className="text-warning">*</span> : null}：
              </span>
              <div className="flex flex-wrap items-center gap-3">
                {options.map((option, idx) => (
                  <label key={option.id || idx} className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      isSelected={isCorrectAnswer(option.id)}
                      onChange={(next) => handleCorrectAnswerToggle(option.id, next)}
                      isDisabled={disabled || !option.label?.trim()}
                    >
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                    </Checkbox>
                    <span>{option.label?.trim() || option.value || `选项 ${option.id || idx + 1}`}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showSelectionLimits ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <div className="text-sm font-medium text-foreground">最少选择</div>
            <NumberField
              value={question.minSelections ?? 0}
              onChange={(v) => onUpdate({ minSelections: Number.isFinite(v) ? v : 0 })}
              minValue={0}
              maxValue={options.length}
              isDisabled={disabled}
            >
              <NumberField.Group>
                <NumberField.DecrementButton />
                <NumberField.Input />
                <NumberField.IncrementButton />
              </NumberField.Group>
            </NumberField>
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium text-foreground">最多选择</div>
            <NumberField
              value={question.maxSelections ?? options.length}
              onChange={(v) => onUpdate({ maxSelections: Number.isFinite(v) ? v : options.length })}
              minValue={1}
              maxValue={options.length}
              isDisabled={disabled}
            >
              <NumberField.Group>
                <NumberField.DecrementButton />
                <NumberField.Input />
                <NumberField.IncrementButton />
              </NumberField.Group>
            </NumberField>
          </div>
        </div>
      ) : null}

      {showScore ? (
        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground">分值</div>
          <NumberField
            className="w-40"
            value={question.score ?? 0}
            onChange={(v) => onUpdate({ score: Number.isFinite(v) ? v : 0 })}
            minValue={0}
            step={0.5}
            isDisabled={disabled}
          >
            <NumberField.Group>
              <NumberField.DecrementButton />
              <NumberField.Input />
              <NumberField.IncrementButton />
            </NumberField.Group>
          </NumberField>
        </div>
      ) : null}
    </div>
  );
}
